import type { AiCapability, PricingPlan } from "../../types";

// All pricing-page copy, verbatim from the design (C-ids match
// .claude/projects/control-center/subscription/pricing-page-design.json).
// Verified against the zoomed screenshots design-screenshots/3-.png – 8-.png.

// Enterprise plan "Talk to sales" form.
export const SALES_FORM_URL =
  "https://share.hsforms.com/11MS12UO6Sheouq9gNZQ_jAnr2j7";

// DeskDay AI section "Talk to sales" form (PH-6, user 2026-10-27).
export const AI_SALES_FORM_URL =
  "https://share.hsforms.com/13DvorDTZRIy15Ze6-c5JxQnr2j7";

export const PRICING_COPY = {
  plansEyebrow: "Subscription plans", // C1
  plansTitle: "Transparent & flexible pricing", // C2
  plansSubtitle: "Every plan starts with a 14-day free trial", // C3
  mostPopular: "Most popular", // C4
  aiEyebrow: "Optional add-on", // C44
  aiTitle: "DeskDay AI pricing", // C45
  aiPilotNotice:
    "Pilot pricing · 50% off for early customers in the DeskDay AI rollout", // C46
  aiPrice: "$199", // C47
  aiOriginalPrice: "$399", // C48
  aiPeriod: "/month", // C49
  aiFootnote: "*Requires an active DeskDay PSA plan.", // C50
  talkToSales: "Talk to sales", // C18
} as const;

// C21–C37
const STANDARD_FEATURES = [
  "Multi-channel ticketing",
  "Project & task management",
  "Knowledge base",
  "Dispatch portal",
  "Timesheet",
  "Announcement",
  "Contracts & invoicing",
  "Service desk workflow builder",
  "Quality health analysis",
  "SLA and automated time tracking",
  "SSO for customer & technician logins",
  "Custom support mail address (up to 5)",
  "Custom-branded web portal for customers",
  "DeskDay-branded Teams app for customers",
  "DeskDay-branded iOS & Android mobile app for customers",
  "DeskDay-branded iOS & Android mobile app for techs",
  "DeskDay-branded Windows & Mac desktop app for customers",
];

// C38–C43
const ENTERPRISE_FEATURES = [
  "Fully branded Teams app for customers",
  "Fully branded iOS & Android mobile app for customers",
  "Fully branded Windows & Mac desktop app for customers",
  "20 hours of expert onboarding included",
  "Early access to every platform feature & Agents",
  "Higher action limits for AI workforce",
];

export const PRICING_PLANS: PricingPlan[] = [
  {
    key: "monthly",
    name: "Standard plan (monthly)", // C5
    price: 79, // C6
    period: "per tech/month", // C7
    note: "Minimum 2 seats", // C8
    ctaLabel: "Subscribe", // C9
    ctaVariant: "secondary",
    featuresHeading: "Key features", // C19
    features: STANDARD_FEATURES,
  },
  {
    key: "annually",
    name: "Standard plan (annually)", // C10
    price: 59, // C11
    originalPrice: 79, // C12
    period: "per tech/month", // C7
    note: "No minimum seats", // C13
    ctaLabel: "Subscribe", // C9
    ctaVariant: "primary",
    featuresHeading: "Key features", // C19
    features: STANDARD_FEATURES,
    isPopular: true,
  },
  {
    key: "enterprise",
    name: "Enterprise plan", // C14
    price: 1189, // C15
    period: "per month", // C16
    note: "Includes 10 seats. For each additional seat: $69 per seat/month", // C17
    ctaLabel: "Talk to sales", // C18
    ctaVariant: "secondary",
    featuresHeading: "Everything in Standard, plus", // C20
    features: ENTERPRISE_FEATURES,
  },
];

export const AI_CAPABILITIES: AiCapability[] = [
  {
    key: "agentActions",
    title: "AI agent actions/month", // C51
    metrics: [
      { value: "600", label: "Standard plan" }, // C52, C53
      { value: "1,000", label: "Enterprise plan" }, // C54, C14
    ],
    notes: [
      "Additional actions beyond the allotted are $0.25 each, added to your next invoice.", // C55
      "*Fair use. Helena Copilot and Ask Helena don’t use agent actions", // C79 — footnote for "Helena*"
    ],
    details: [
      {
        key: "triage-dispatch",
        tagLabel: "Available now", // C56
        tagTone: "success",
        title: "Triage & dispatch agent", // C57
        description:
          "Asks follow-ups, categorizes and prioritizes tickets, then routes each to the right tech or queue.", // C58
      },
      {
        key: "l0-tech",
        tagLabel: "Coming Soon", // C59
        tagTone: "orange",
        tagHasDot: true,
        title: "L0 Tech agent", // C60
        description:
          "Resolves suitable Level 0 requests before a technician steps in.", // C61
      },
      {
        key: "m365",
        tagLabel: "Coming Soon", // C59
        tagTone: "orange",
        tagHasDot: true,
        title: "Microsoft 365 agent", // C62
        description:
          "Handles password resets, MFA resets, and user changes in Microsoft 365.", // C63
      },
    ],
  },
  {
    key: "helena",
    title: "Helena*", // C65
    metrics: [
      { value: "AI copilot", label: "Unlimited" }, // C66, C67
      { value: "Ask Helena conversations", label: "Unlimited" }, // C68, C67
    ],
    details: [
      {
        key: "helena-copilot",
        tagLabel: "Included", // C69
        tagTone: "success",
        title: "Helena AI copilot", // C70
        features: [
          "Draft and refine customer replies", // C71
          "Real-time customer sentiment and urgency", // C72
          "Root cause and step-by-step fixes from past tickets, KB and vendor docs, with a confidence score", // C73
        ],
      },
      {
        key: "ask-helena",
        tagLabel: "Included", // C69
        tagTone: "success",
        title: "Ask Helena", // C74
        features: [
          "Get answers across all your DeskDay data", // C75
          "Create, update and assign tickets in conversation", // C76
          "Voice dictation", // C77
          "QBRs, SLA insights and trends on request", // C78
        ],
      },
    ],
  },
];

// Shared by the cancelled and expired banners (cancelled-banner-design.json C4).
export const SUBSCRIPTION_BANNER_COPY = {
  contactSales: "Contact sales",
} as const;

// Cancelled-subscription banner (cancelled-banner-design.json C1, C3),
// split around the date so the date can be semibold.
export const CANCELLED_BANNER_COPY = {
  beforeDate:
    "Your subscription was cancelled but we have kept the door open until",
  afterDate:
    ". Renew before this date to experience DeskDay without any interruptions!",
} as const;

// Expired-subscription banner (no-subscription screen): the existing copy,
// unchanged by the restyle.
export const EXPIRED_BANNER_COPY = {
  message:
    "Your plan has expired. To maintain access to our features, please renew your subscription.",
} as const;
